import dayjs from "dayjs";
import { categoryIconMap } from "../constants/constants";
export const formatTitle = (title: string = "") =>
  title
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const normalizeProsCons = (prosCons: string | string[] | undefined) => {
  if (!prosCons) return [];
  try {
    // If it's a JSON string like '["wonderful","simple"]'
    if (typeof prosCons === "string" && prosCons.trim().startsWith("[")) {
      return JSON.parse(prosCons);
    }

    // If it's already an array
    if (Array.isArray(prosCons)) return prosCons;

    // Otherwise, handle string splitting
    if (typeof prosCons === "string") {
      if (prosCons.includes("/")) {
        return prosCons
          .split("/")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      if (prosCons.includes("\n")) {
        return prosCons
          .split("\n")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return prosCons
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  } catch {
    return [];
  }
};

/**
 * Sorts subcategories based on predefined order for each category
 * @param categoryName - The parent category name
 * @param subcategories - Array of subcategory objects
 * @returns Sorted array with matched items first (in order), unmatched at end
 */
const transformSubcategories = (categoryName: string, subcategories: any[]) => {
  const subcategoryOrder: Record<string, string[]> = {
    "Technology & IT": [
      "IT Networking & Infrastructure",
      "Cybersecurity & Data Protection",
      "Cloud Computing & Storage",
      "AI, Machine Learning & Automation",
      "Software development & DevOps",
      "Data & Analytics",
      "Business Process Management System",
      "Enterprise Applications",
    ],
    "Business & Operations": [
      "Marketing & Advertising Technology",
      "Finance & Accounting Technology",
      "HR & Workforce Management",
      "Collaboration & Productivity Tools",
      "Supply Chain & Logistics Tech",
      "Legal & Compliance Tech",
    ],
    "Industry Specific": [
      "Healthcare & Life Sciences Technology",
      "E-commerce & Retail Technology",
      "Industrial Automation",
      "Energy, Sustainability & GreenTech",
      "Fintech",
      "EdTech & Learning Tech",
      "Media, Publishing & Creative Solutions",
    ],
  };

  const order = subcategoryOrder[categoryName];
  if (!order || !subcategories) return subcategories;

  // Create lowercase version of order array for case-insensitive matching
  const orderLower = order.map((name) => name.toLowerCase());

  return [...subcategories].sort((a, b) => {
    const indexA = orderLower.indexOf(a.name?.trim().toLowerCase());
    const indexB = orderLower.indexOf(b.name?.trim().toLowerCase());

    // If both are in the order array, sort by their index
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    // If only a is in the order, a comes first
    if (indexA !== -1) return -1;
    // If only b is in the order, b comes first
    if (indexB !== -1) return 1;
    // If neither is in the order, maintain original order
    return 0;
  });
};

/**
 * @param data - array of categories
 * @returns array of objects with label, icon, documentId, and subitems
 * @description transforms categories for mega menu as per the structure required by the mega menu component this is for redirecting to products page
 * @example
 * transformCategories(productCategories)
 * @returns
 * [
 *  {
 *      label: "CRM Software",
 *      icon: "icons.default",
 *      documentId: "123",
 *      subitems: [
 *          {
 *              label: "CRM Software",
 *              href: "/products/123",
 *              documentId: "123",
 *          },
 *      ],
 *  },
 *]
 */
export const transformCategories = (data: any[]) => {
  console.log("data in transformCategories", data);
  const categoryOrder = ["Technology & IT", "Business & Operations", "Industry Specific"];

  const res = data.map((category) => ({
    name: category.name,
    icon: categoryIconMap[category.name] || "icons.default",
    documentId: category.documentId, // keep documentId at category level
    subcategories: transformSubcategories(category.name, category?.subcategories)?.map((sub: any) => ({
      name: sub.name.trim(),
      href: `/products?category=${category.documentId}&subCategory=${sub.documentId}`,
      documentId: sub.documentId, // keep documentId at subitem level
      contentCount: sub.contentCount,
    })),
  }));

  const sortedRes = res.sort((a, b) => {
    const indexA = categoryOrder.indexOf(a.name);
    const indexB = categoryOrder.indexOf(b.name);
    // Push unknown categories to the end
    return (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB);
  });

  console.log("res in transformCategories", sortedRes);
  return sortedRes;
};

//debounce function
export function debounce<T extends (...args: any[]) => void>(
  func: T,
  timeout = 300
) {
  let timer: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(null, args); // no need for `this` in React
    }, timeout);
  };
}

export function formatDate(isoDate?: string, format?: string): string {
  if (!isoDate) return ""; // handle null/undefined

  const date = dayjs(isoDate);
  if (!date.isValid()) return ""; // handle invalid dates

  return date.format(format || "ddd, D MMM, YYYY");
}

export function parseImageString(imageString: string) {
  if (!imageString) return [];

  try {
    const parsed = JSON.parse(imageString);
    if (Array.isArray(parsed)) {
      return parsed;
    }
    return [];
  } catch (error) {
    console.error("Invalid image string:", error);
    return [];
  }
}

// utils/normalizeImageSrc.ts
export function normalizeImageSrc(src: string): string[] {
  if (!src) return [];

  // Remove [ ] brackets
  const cleaned = src.replace(/^\[|\]$/g, "");

  // Split by comma in case there are multiple URLs
  return cleaned
    .split(",")
    .map((u) => u.trim().replace(/['"]+/g, "")) // remove quotes and spaces
    .filter((u) => u.length > 0); // keep only valid strings
}

//convert string to dash separated slug
export function generateUrlSlug(title: string, id?: string | number): string {
  if (!title) return "";

  // Convert to lowercase, remove special characters, replace spaces with hyphens
  const slug = title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // remove non-word chars
    .replace(/\s+/g, "-"); // replace spaces with '-'

  // Append ID if provided
  return id ? `${slug}/${id}` : slug;
}

//convert dash separated slug to string and to retrieve id
export function parseSlug(slug: string) {
  const parts = slug.split("/");
  const id = parts.pop();
  const title = parts.join(" ").replace(/-/g, " ");
  return { title, id };
}

export function getFirstSubcategoryWithContent(
  categories: any[],
  categoryId: string = categories[0]?.documentId,
  content?: string
) {
  const pathname = window.location.pathname;
  let contentType = content || "";
  if (pathname?.includes("compare-tools") || pathname?.includes("products")) {
    contentType = "product";
  }
  const selectedCat = categories?.find((cat) => cat?.documentId == categoryId);
  const selectedSubCat = selectedCat?.subcategories?.find(
    (subCat: any) => subCat?.contentCount[contentType] > 0
  )?.documentId;
  return selectedSubCat;
}
